import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, BookOpen, Users, Trophy } from "lucide-react";
import PageHeader from "../../components/common/PageHeader";
import { schoolDivisions } from "../../data/schoolInfo";
import collegeImg from "../../assets/college.jpg";

const CollegeOverview = () => {
  const school = schoolDivisions.college;

  const stats = [
    { value: "95%", label: "University Placement" },
    { value: "500+", label: "Students" },
    { value: "30+", label: "Teachers" },
    { value: "15+", label: "Subjects" },
  ];

  const features = [
    {
      icon: Award,
      title: "Academic Excellence",
      description:
        "Rigorous curriculum preparing students for WAEC, NECO, and beyond.",
    },
    {
      icon: BookOpen,
      title: "Diverse Subjects",
      description:
        "Comprehensive subject offerings in sciences, arts, and commercial.",
    },
    {
      icon: Users,
      title: "Expert Faculty",
      description: "Highly qualified teachers dedicated to student success.",
    },
    {
      icon: Trophy,
      title: "Proven Results",
      description: "Consistent top performance in national examinations.",
    },
  ];

  return (
    <>
      <PageHeader
        title={school.name}
        subtitle={school.tagline}
        breadcrumbs={[
          { label: "Our Schools", href: "/our-schools" },
          { label: "College" },
        ]}
        backgroundImage={collegeImg}
      />

      {/* Stats */}
      {/* <section className="bg-primary-600 py-12">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-primary-200">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Introduction */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
                {school.ageRange}
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Preparing Leaders for Tomorrow
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Stirret College provides a challenging and supportive academic
                environment where students develop the knowledge, skills, and
                character needed to succeed in higher education and beyond.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our comprehensive curriculum, combined with diverse
                extracurricular activities, ensures students receive a
                well-rounded education that prepares them for the demands of the
                modern world.
              </p>
              <Link
                to="/college/academics"
                className="btn-primary group inline-flex"
              >
                Explore Academics
                <ArrowRight
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={collegeImg}
                alt="Stirret College"
                className="w-full h-[500px] object-cover rounded-2xl shadow-hard"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-soft text-center"
                >
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-primary-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary-600">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Join the Stirret College Family
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Take the next step towards academic excellence and personal growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/college/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
            >
              Schedule a Visit
            </Link>
            <Link
              to="/admissions"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CollegeOverview;
